import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Sequence,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {BrandIntro} from '../components/BrandIntro';

export type Highlight = {title: string; description: string};
export type AppStorePreviewProps = {
  tagline: string;
  highlights: [Highlight, Highlight, Highlight];
  cta: string;
};

const HighlightSlide: React.FC<{
  title: string;
  description: string;
  index: number;
}> = ({title, description, index}) => {
  const frame = useCurrentFrame();
  const numOpacity = interpolate(frame, [0, 12], [0, 1], {extrapolateRight: 'clamp'});
  const titleOpacity = interpolate(frame, [10, 25], [0, 1], {extrapolateRight: 'clamp'});
  const titleY = interpolate(frame, [10, 25], [30, 0], {extrapolateRight: 'clamp'});
  const descOpacity = interpolate(frame, [25, 45], [0, 1], {extrapolateRight: 'clamp'});

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 80px',
        gap: 28,
      }}
    >
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 48,
          fontWeight: 900,
          color: brand.action,
          letterSpacing: 6,
          opacity: numOpacity,
        }}
      >
        {`0${index + 1} / 03`}
      </div>
      <div
        style={{
          textAlign: 'center',
          fontFamily: brandFont,
          fontSize: 96,
          fontWeight: 900,
          color: brand.textPrimary,
          letterSpacing: -3,
          lineHeight: 1.05,
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
        }}
      >
        {title}
      </div>
      <div
        style={{
          textAlign: 'center',
          fontFamily: brandFont,
          fontSize: 38,
          color: brand.textSecondary,
          opacity: descOpacity,
          lineHeight: 1.3,
        }}
      >
        {description}
      </div>
    </AbsoluteFill>
  );
};

const CTASlide: React.FC<{cta: string}> = ({cta}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const scale = spring({frame, fps, from: 0.7, to: 1, config: {damping: 12}});
  return (
    <AbsoluteFill style={{justifyContent: 'center', alignItems: 'center'}}>
      <div
        style={{
          transform: `scale(${scale})`,
          background: brand.primary,
          color: brand.textPrimary,
          fontFamily: brandFont,
          fontWeight: 900,
          fontSize: 80,
          padding: '36px 80px',
          borderRadius: 48,
          letterSpacing: -1,
        }}
      >
        {cta}
      </div>
    </AbsoluteFill>
  );
};

export const AppStorePreview: React.FC<AppStorePreviewProps> = ({
  tagline,
  highlights,
  cta,
}) => (
  <AbsoluteFill>
    <BgGradient />
    <Audio src={staticFile('music/app-store-preview.mp3')} volume={0.4} />
    <Sequence  durationInFrames={90}>
      <BrandIntro tagline={tagline} />
    </Sequence>
    {highlights.map((h, i) => (
      <Sequence key={`h-${i}`} from={90 + i * 210} durationInFrames={210}>
        <HighlightSlide title={h.title} description={h.description} index={i} />
      </Sequence>
    ))}
    <Sequence from={720} durationInFrames={180}>
      <CTASlide cta={cta} />
    </Sequence>
  </AbsoluteFill>
);
